/**
 * GCLeaderboard — "Ranking de Empreiteiras": top General Contractors by
 * federal construction award value (USASpending.gov, last N months).
 * Tabs: Ranking (click row → award history) · Seguindo (follows + alerts).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Trophy, Building2, ExternalLink, RefreshCcw, X, Star, Bell,
  Mail, Trash2,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ["", "FL", "TX", "CA", "NY", "GA", "NV", "NC", "AZ", "VA", "NJ", "MA"];

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

export default function GCLeaderboard() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [tab, setTab] = useState("ranking");
  const [months, setMonths] = useState(12);
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [follows, setFollows] = useState([]);
  const [followedNames, setFollowedNames] = useState(new Set());

  const load = async (m = months, s = state) => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/gc-leaderboard`, {
        params: { months: m, state: s, limit: 25 },
      });
      setData(d);
    } catch (_e) {
      toast.error(isPt ? "Falha ao carregar ranking" : "Failed to load leaderboard");
    } finally { setLoading(false); }
  };
  const loadFollows = async () => {
    try {
      const { data: d } = await axios.get(`${API}/gc-follows`);
      setFollows(d.items || []);
      setFollowedNames(new Set((d.items || []).map((f) => f.name.toLowerCase())));
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); loadFollows(); }, []); // eslint-disable-line

  const toggleFollow = async (name) => {
    const isFollowing = followedNames.has(name.toLowerCase());
    try {
      if (isFollowing) {
        const f = follows.find((x) => x.name.toLowerCase() === name.toLowerCase());
        if (f) await axios.delete(`${API}/gc-follows/${f.id}`);
        toast.success(isPt ? `Deixou de seguir ${name}` : `Unfollowed ${name}`);
      } else {
        await axios.post(`${API}/gc-follows`, { name });
        toast.success(isPt ? `⭐ Seguindo ${name} — você será alertado sobre novos contratos` : `⭐ Following ${name}`);
      }
      loadFollows();
    } catch (_e) {
      toast.error(isPt ? "Falha ao atualizar" : "Update failed");
    }
  };

  const openCompany = async (name) => {
    setDetail({ name, loading: true });
    setDetailLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/gc-leaderboard/company`, {
        params: { name, months, state },
      });
      setDetail(d);
    } catch (_e) {
      toast.error(isPt ? "Falha ao carregar histórico" : "Failed to load history");
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  return (
    <div className="flex min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="gc-leaderboard-page">
      <Sidebar />
      <main className="flex-1 min-w-0 px-6 py-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <header className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div>
              <h1 className="text-[22px] font-extrabold inline-flex items-center gap-2">
                <Trophy className="w-6 h-6 text-[#C9A24E]" />
                {isPt ? "Ranking de Empreiteiras" : "GC Leaderboard"}
              </h1>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1">
                {isPt
                  ? "General Contractors que mais venceram contratos federais de construção (USASpending.gov)"
                  : "General Contractors with the most federal construction wins (USASpending.gov)"}
              </p>
            </div>
            {tab === "ranking" && (
              <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                <select value={months} onChange={(e) => { setMonths(Number(e.target.value)); load(Number(e.target.value), state); }}
                  className="al-input h-10 text-[12.5px] flex-1 min-w-[120px] lg:flex-none" data-testid="gcl-months">
                  {[6, 12, 18, 24].map((m) => (
                    <option key={m} value={m}>{m} {isPt ? "meses" : "months"}</option>
                  ))}
                </select>
                <select value={state} onChange={(e) => { setState(e.target.value); load(months, e.target.value); }}
                  className="al-input h-10 text-[12.5px] flex-1 min-w-[120px] lg:flex-none" data-testid="gcl-state">
                  {US_STATES.map((s) => (
                    <option key={s || "all"} value={s}>{s || (isPt ? "Todos os estados" : "All states")}</option>
                  ))}
                </select>
                <button type="button" data-testid="gcl-refresh" onClick={() => load()}
                  className="h-10 w-10 shrink-0 rounded-lg border border-[var(--al-border)] inline-flex items-center justify-center hover:bg-[var(--al-card)]">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                </button>
              </div>
            )}
          </header>

          <div className="flex gap-2 border-b border-[var(--al-border)] mb-5">
            {[
              { id: "ranking", label: isPt ? "Ranking" : "Leaderboard" },
              { id: "following", label: `${isPt ? "Seguindo" : "Following"} (${follows.length})` },
            ].map((t) => (
              <button key={t.id} type="button" data-testid={`gcl-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-[13px] font-semibold transition border-b-2 ${
                  tab === t.id
                    ? "text-[var(--al-text)] border-[#C9A24E]"
                    : "text-[var(--al-text-muted)] border-transparent hover:text-[var(--al-text)]"}`}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === "ranking" && (
            <>
              {data?.cached && (
                <p className="text-[10.5px] text-[var(--al-text-muted)] mb-2">
                  {isPt ? "⚡ Resultado em cache (24h)" : "⚡ Cached result (24h)"}
                </p>
              )}
              <div className="al-card overflow-hidden hidden md:block" data-testid="gcl-table">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-[var(--al-border)] text-left text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)]">
                      <th className="px-4 py-3 w-12">#</th>
                      <th className="px-4 py-3">{isPt ? "Empresa" : "Company"}</th>
                      <th className="px-4 py-3 text-right">{isPt ? "Projetos vencidos" : "Projects won"}</th>
                      <th className="px-4 py-3 text-right">{isPt ? "Valor total" : "Total value"}</th>
                      <th className="px-4 py-3 w-32 text-right" />
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--al-text-muted)]">
                        <Loader2 className="w-5 h-5 animate-spin inline" /> {isPt ? "Consultando USASpending..." : "Querying USASpending..."}
                      </td></tr>
                    )}
                    {!loading && (data?.items || []).map((gc) => {
                      const isFollowed = followedNames.has(gc.name.toLowerCase());
                      return (
                        <tr key={gc.rank} data-testid={`gcl-row-${gc.rank}`}
                          className="border-b border-[var(--al-border)] last:border-0 hover:bg-[var(--al-bg)] transition">
                          <td className="px-4 py-3 font-extrabold text-[var(--al-text-muted)]">
                            {gc.rank <= 3 ? ["🥇", "🥈", "🥉"][gc.rank - 1] : gc.rank}
                          </td>
                          <td className="px-4 py-3 font-semibold cursor-pointer" onClick={() => openCompany(gc.name)}>
                            <span className="inline-flex items-center gap-2">
                              <Building2 className="w-3.5 h-3.5 text-[#1B3A8A] shrink-0" /> {gc.name}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-bold">{gc.projects_won}</td>
                          <td className="px-4 py-3 text-right font-extrabold text-[#1B3A8A]">{fmtMoney(gc.total_value)}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button type="button" data-testid={`gcl-follow-${gc.rank}`}
                              onClick={() => toggleFollow(gc.name)}
                              title={isFollowed ? (isPt ? "Deixar de seguir" : "Unfollow") : (isPt ? "Seguir e receber alertas" : "Follow for alerts")}
                              className={`h-7 w-7 rounded-md border inline-flex items-center justify-center mr-2 transition ${
                                isFollowed
                                  ? "bg-[#C9A24E]/15 border-[#C9A24E] text-[#C9A24E]"
                                  : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#C9A24E] hover:text-[#C9A24E]/50"}`}>
                              <Star className={`w-3.5 h-3.5 ${isFollowed ? "fill-current" : ""}`} />
                            </button>
                            <button type="button" onClick={() => openCompany(gc.name)}
                              className="text-[11px] text-[#3B82F6] font-semibold hover:underline" data-testid={`gcl-detail-${gc.rank}`}>
                              {isPt ? "Histórico →" : "History →"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {!loading && (data?.items || []).length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--al-text-muted)]">
                        {isPt ? "Nenhum resultado no período." : "No results for this period."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile: each row becomes a vertical card (< 768px) */}
              <div className="md:hidden space-y-2.5" data-testid="gcl-cards-mobile">
                {loading && (
                  <div className="al-card p-8 text-center text-[var(--al-text-muted)]">
                    <Loader2 className="w-5 h-5 animate-spin inline" /> {isPt ? "Consultando USASpending..." : "Querying USASpending..."}
                  </div>
                )}
                {!loading && (data?.items || []).map((gc) => {
                  const isFollowed = followedNames.has(gc.name.toLowerCase());
                  return (
                    <div key={gc.rank} className="al-card p-4" data-testid={`gcl-card-${gc.rank}`}>
                      <div className="flex items-start gap-3">
                        <span className="shrink-0 w-9 h-9 rounded-lg bg-[#C9A24E]/12 text-[#92660C] flex items-center justify-center text-[14px] font-extrabold">
                          {gc.rank <= 3 ? ["🥇", "🥈", "🥉"][gc.rank - 1] : `#${gc.rank}`}
                        </span>
                        <button type="button" onClick={() => openCompany(gc.name)}
                          className="flex-1 min-w-0 text-left">
                          <div className="text-[13.5px] font-bold leading-snug break-words">
                            {gc.name}
                          </div>
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="rounded-lg bg-[var(--al-row-alt)] px-3 py-2">
                          <div className="text-[9.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-bold">
                            {isPt ? "Projetos vencidos" : "Projects won"}
                          </div>
                          <div className="text-[16px] font-extrabold tabular-nums">{gc.projects_won}</div>
                        </div>
                        <div className="rounded-lg bg-[var(--al-row-alt)] px-3 py-2">
                          <div className="text-[9.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-bold">
                            {isPt ? "Valor total" : "Total value"}
                          </div>
                          <div className="text-[16px] font-extrabold text-[#1B3A8A] tabular-nums">{fmtMoney(gc.total_value)}</div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button type="button" data-testid={`gcl-card-follow-${gc.rank}`}
                          onClick={() => toggleFollow(gc.name)}
                          className={`flex-1 h-11 rounded-lg border text-[12px] font-bold inline-flex items-center justify-center gap-1.5 transition ${
                            isFollowed
                              ? "bg-[#C9A24E]/15 border-[#C9A24E] text-[#C9A24E]"
                              : "border-[var(--al-border)] text-[var(--al-text-muted)]"}`}>
                          <Star className={`w-3.5 h-3.5 ${isFollowed ? "fill-current" : ""}`} />
                          {isFollowed ? (isPt ? "Seguindo" : "Following") : (isPt ? "Seguir" : "Follow")}
                        </button>
                        <button type="button" data-testid={`gcl-card-detail-${gc.rank}`}
                          onClick={() => openCompany(gc.name)}
                          className="flex-1 h-11 rounded-lg bg-[#1B3A8A] text-white text-[12px] font-bold inline-flex items-center justify-center">
                          {isPt ? "Histórico →" : "History →"}
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!loading && (data?.items || []).length === 0 && (
                  <div className="al-card p-8 text-center text-[12.5px] text-[var(--al-text-muted)]">
                    {isPt ? "Nenhum resultado no período." : "No results for this period."}
                  </div>
                )}
              </div>
            </>
          )}

          {tab === "following" && (
            <FollowingTab isPt={isPt} follows={follows} onChanged={loadFollows}
              onOpenCompany={openCompany} />
          )}
        </div>
      </main>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl bg-[var(--al-card)] h-full overflow-y-auto shadow-2xl p-4 sm:p-6 pb-24 lg:pb-6"
            onClick={(e) => e.stopPropagation()} data-testid="gcl-company-drawer">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-[17px] font-extrabold">{detail.name}</h2>
                {!detailLoading && (
                  <p className="text-[12px] text-[var(--al-text-muted)] mt-0.5">
                    {detail.count} {isPt ? "contratos" : "awards"} · {fmtMoney(detail.total_value)} · {isPt ? `últimos ${months} meses` : `last ${months} months`}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" data-testid="gcl-drawer-follow"
                  onClick={() => toggleFollow(detail.name)}
                  className={`h-8 px-3 rounded-md border text-[11.5px] font-bold inline-flex items-center gap-1.5 ${
                    followedNames.has((detail.name || "").toLowerCase())
                      ? "bg-[#C9A24E]/15 border-[#C9A24E] text-[#C9A24E]"
                      : "border-[var(--al-border)] hover:border-[#C9A24E] hover:text-[#C9A24E]"}`}>
                  <Star className={`w-3.5 h-3.5 ${followedNames.has((detail.name || "").toLowerCase()) ? "fill-current" : ""}`} />
                  {followedNames.has((detail.name || "").toLowerCase())
                    ? (isPt ? "Seguindo" : "Following")
                    : (isPt ? "Seguir" : "Follow")}
                </button>
                <button type="button" data-testid="gcl-drawer-close" onClick={() => setDetail(null)}
                  className="h-8 w-8 rounded-md border border-[var(--al-border)] inline-flex items-center justify-center hover:bg-[var(--al-bg)]">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {detailLoading ? (
              <div className="py-14 text-center text-[var(--al-text-muted)]">
                <Loader2 className="w-6 h-6 animate-spin inline" />
              </div>
            ) : (
              <div className="space-y-3">
                {(detail.awards || []).map((a, i) => (
                  <div key={a.award_id || i} className="al-card p-3.5" data-testid={`gcl-award-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-semibold leading-snug">{a.description || a.award_id}</div>
                        <div className="text-[11px] text-[var(--al-text-muted)] mt-1">
                          {a.agency} · {a.state || "—"} · {a.start_date} → {a.end_date}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[14px] font-extrabold text-[#1B3A8A]">{fmtMoney(a.amount)}</div>
                        {a.usaspending_url && (
                          <a href={a.usaspending_url} target="_blank" rel="noopener noreferrer"
                            className="text-[10.5px] text-[#3B82F6] inline-flex items-center gap-0.5 hover:underline">
                            <ExternalLink className="w-2.5 h-2.5" /> USASpending
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(detail.awards || []).length === 0 && (
                  <p className="text-[12.5px] text-[var(--al-text-muted)] py-8 text-center">
                    {isPt ? "Nenhum contrato encontrado." : "No awards found."}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FollowingTab({ isPt, follows, onChanged, onOpenCompany }) {
  const [prefs, setPrefs] = useState({ instant: true, daily_digest: false });
  const [alerts, setAlerts] = useState([]);
  const [checking, setChecking] = useState(false);

  const loadExtras = async () => {
    try {
      const [{ data: p }, { data: a }] = await Promise.all([
        axios.get(`${API}/gc-follows/prefs`),
        axios.get(`${API}/gc-follows/alerts`),
      ]);
      setPrefs(p); setAlerts(a.items || []);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { loadExtras(); }, []);

  const savePrefs = async (next) => {
    setPrefs(next);
    try {
      await axios.post(`${API}/gc-follows/prefs`, next);
      toast.success(isPt ? "Preferências salvas" : "Preferences saved");
    } catch (_e) { toast.error(isPt ? "Falha ao salvar" : "Save failed"); }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/gc-follows/check-now`);
      toast.success(isPt
        ? `Verificado: ${data.checked} empresas, ${data.new_alerts} novidade(s)`
        : `Checked ${data.checked} companies, ${data.new_alerts} new alert(s)`);
      loadExtras(); onChanged();
    } catch (_e) { toast.error(isPt ? "Falha na verificação" : "Check failed"); }
    finally { setChecking(false); }
  };

  const unfollow = async (f) => {
    try {
      await axios.delete(`${API}/gc-follows/${f.id}`);
      toast.success(isPt ? `Deixou de seguir ${f.name}` : `Unfollowed ${f.name}`);
      onChanged();
    } catch (_e) { toast.error("Erro"); }
  };

  return (
    <div className="space-y-5" data-testid="gcl-following-tab">
      <div className="al-card p-4 flex flex-wrap items-center gap-5">
        <span className="text-[12px] font-bold inline-flex items-center gap-1.5">
          <Mail className="w-4 h-4 text-[#1B3A8A]" /> {isPt ? "Notificações por e-mail:" : "Email notifications:"}
        </span>
        <label className="inline-flex items-center gap-2 text-[12.5px] cursor-pointer">
          <input type="checkbox" checked={prefs.instant} data-testid="gcl-pref-instant"
            onChange={(e) => savePrefs({ ...prefs, instant: e.target.checked })}
            className="accent-[#C9A24E] w-4 h-4" />
          {isPt ? "Instantâneo (na hora que detectar)" : "Instant"}
        </label>
        <label className="inline-flex items-center gap-2 text-[12.5px] cursor-pointer">
          <input type="checkbox" checked={prefs.daily_digest} data-testid="gcl-pref-digest"
            onChange={(e) => savePrefs({ ...prefs, daily_digest: e.target.checked })}
            className="accent-[#C9A24E] w-4 h-4" />
          {isPt ? "Resumo diário" : "Daily digest"}
        </label>
        <button type="button" data-testid="gcl-check-now" onClick={checkNow} disabled={checking}
          className="ml-auto h-9 px-3.5 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
          {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
          {isPt ? "Verificar agora" : "Check now"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div>
          <h3 className="text-[13px] font-bold mb-2">{isPt ? "Empreiteiras seguidas" : "Followed contractors"}</h3>
          {follows.length === 0 && (
            <p className="text-[12.5px] text-[var(--al-text-muted)] al-card p-4">
              {isPt ? "Você ainda não segue nenhuma empreiteira. Clique na ⭐ no Ranking." : "You are not following anyone yet. Click the ⭐ in the Leaderboard."}
            </p>
          )}
          <div className="space-y-2">
            {follows.map((f) => (
              <div key={f.id} className="al-card p-3 flex items-center gap-3" data-testid={`gcl-follow-item-${f.id}`}>
                <Star className="w-4 h-4 text-[#C9A24E] fill-current shrink-0" />
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onOpenCompany(f.name)}>
                  <div className="text-[13px] font-semibold truncate">{f.name}</div>
                  <div className="text-[10.5px] text-[var(--al-text-muted)]">
                    {f.awards_tracked} {isPt ? "contratos monitorados" : "awards tracked"}
                    {f.last_checked_at && ` · ${isPt ? "verificado" : "checked"} ${new Date(f.last_checked_at).toLocaleDateString("pt-BR")}`}
                  </div>
                </div>
                <button type="button" data-testid={`gcl-unfollow-${f.id}`} onClick={() => unfollow(f)}
                  className="h-7 w-7 rounded-md border border-[var(--al-border)] inline-flex items-center justify-center text-[var(--al-text-muted)] hover:text-rose-500 hover:border-rose-300">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-[13px] font-bold mb-2">{isPt ? "Alertas recentes" : "Recent alerts"}</h3>
          {alerts.length === 0 && (
            <p className="text-[12.5px] text-[var(--al-text-muted)] al-card p-4">
              {isPt ? "Nenhum alerta ainda — você será notificado quando uma empreiteira seguida vencer um novo contrato." : "No alerts yet."}
            </p>
          )}
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} className="al-card p-3.5" data-testid={`gcl-alert-${a.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-[12.5px] font-bold inline-flex items-center gap-1.5">
                    🏆 {a.company}
                  </span>
                  <span className="text-[12px] font-extrabold text-[#1B3A8A]">{fmtMoney(a.total_value)}</span>
                </div>
                <ul className="text-[11.5px] space-y-1">
                  {(a.awards || []).slice(0, 3).map((aw, i) => (
                    <li key={`aw-${i}`} className="truncate">
                      <b>{fmtMoney(aw.amount)}</b> — {aw.description || aw.award_id}
                    </li>
                  ))}
                </ul>
                <div className="text-[10px] text-[var(--al-text-muted)] mt-1.5">
                  {new Date(a.created_at).toLocaleString("pt-BR")}
                  {a.emailed && " · 📧 e-mail enviado"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
